import type { SvgIconComponent } from "@mui/icons-material";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import CallMergeOutlinedIcon from "@mui/icons-material/CallMergeOutlined";
import CallSplitOutlinedIcon from "@mui/icons-material/CallSplitOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import GroupAddOutlinedIcon from "@mui/icons-material/GroupAddOutlined";
import GroupRemoveOutlinedIcon from "@mui/icons-material/GroupRemoveOutlined";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import HistoryToggleOffOutlinedIcon from "@mui/icons-material/HistoryToggleOffOutlined";
import HowToRegOutlinedIcon from "@mui/icons-material/HowToRegOutlined";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import LinkOffOutlinedIcon from "@mui/icons-material/LinkOffOutlined";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import PersonRemoveOutlinedIcon from "@mui/icons-material/PersonRemoveOutlined";
import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import SyncAltOutlinedIcon from "@mui/icons-material/SyncAltOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import type { AccentIconTileTone } from "@/ui/molecules/AccentIconTile";
import { type LedgerEntry, RequestOriginGroup, RequestOriginKind, RequestOriginsStatus } from "@/utils/models";
import { ORIGIN_GROUP_BY_KIND } from "@/utils/requestOrigins";

/** The glyph is the kind: its shape alone says what changed the slots. */
export const ORIGIN_KIND_ICON: Readonly<Record<RequestOriginKind, SvgIconComponent>> = {
	[RequestOriginKind.CallOff]: PersonRemoveOutlinedIcon,
	[RequestOriginKind.TimeOff]: EventBusyOutlinedIcon,
	[RequestOriginKind.Deactivation]: PersonOffOutlinedIcon,
	[RequestOriginKind.AutoPost]: BoltOutlinedIcon,
	[RequestOriginKind.FromRecommendation]: LightbulbOutlinedIcon,
	[RequestOriginKind.PostedByPerson]: PersonOutlineOutlinedIcon,
	[RequestOriginKind.PostedByAnnie]: AutoAwesomeOutlinedIcon,
	[RequestOriginKind.ReplaceRequest]: SwapHorizOutlinedIcon,
	[RequestOriginKind.SwapRequest]: SwapHorizOutlinedIcon,
	[RequestOriginKind.ReplacingAgency]: SyncAltOutlinedIcon,
	[RequestOriginKind.Agency]: HubOutlinedIcon,
	[RequestOriginKind.AgencyCanceled]: LinkOffOutlinedIcon,
	[RequestOriginKind.CountRaised]: GroupAddOutlinedIcon,
	[RequestOriginKind.CountLowered]: GroupRemoveOutlinedIcon,
	[RequestOriginKind.MergedIn]: CallMergeOutlinedIcon,
	[RequestOriginKind.CountAdjusted]: TuneOutlinedIcon,
	[RequestOriginKind.Filled]: HowToRegOutlinedIcon,
	[RequestOriginKind.Canceled]: CancelOutlinedIcon,
	[RequestOriginKind.MergedAway]: CallSplitOutlinedIcon,
	[RequestOriginKind.AgencySyncLowered]: GroupRemoveOutlinedIcon,
	[RequestOriginKind.NotRecorded]: HistoryToggleOffOutlinedIcon,
	[RequestOriginKind.Unknown]: HelpOutlineOutlinedIcon,
};

/** The tile's tone is the group — shown only once a card is open, so the list stays colourless. */
export const ORIGIN_GROUP_TONE: Readonly<Record<RequestOriginGroup, AccentIconTileTone>> = {
	[RequestOriginGroup.Automation]: "primary",
	[RequestOriginGroup.People]: "neutral",
	[RequestOriginGroup.External]: "info",
};

/** How an entry's tile is drawn: an opening in its group's tone, a removal neutral, a change with no recorded cause outlined. */
export type EntryTileLook = { tone: AccentIconTileTone; dashed: boolean };

export const entryTileLook = (entry: LedgerEntry): EntryTileLook => {
	if (entry.kind === RequestOriginKind.NotRecorded || entry.kind === RequestOriginKind.Unknown) return { tone: "neutral", dashed: true };
	const group = entry.delta > 0 ? ORIGIN_GROUP_BY_KIND[entry.kind] : null;
	return { tone: group === null ? "neutral" : ORIGIN_GROUP_TONE[group], dashed: false };
};

/** Where an origin was looked at, for `request_origin_viewed`. */
export enum RequestOriginViewLevel {
	List = "list",
	Gaps = "gaps",
}

/** The Origin filter's state, as the Requests screen owns it, with the per-group counts for its current scope. */
export type OriginFilterControl = {
	selected: readonly RequestOriginGroup[];
	onChange: (next: RequestOriginGroup[]) => void;
	counts: Readonly<Record<RequestOriginGroup, number>>;
};

/** A choice made while origins were in, kept (and still applied once they are back) while they reload or fail. */
type HeldOriginSelection = { selected: readonly RequestOriginGroup[]; clear: () => void };

/**
 * Where the Origin filter stands: hidden where the department gets no origins, a skeleton while they load, a retry
 * notice when they failed, the filter once they are in.
 */
export type OriginFilterState =
	| { status: RequestOriginsStatus.Unavailable }
	| ({ status: RequestOriginsStatus.Loading } & HeldOriginSelection)
	| ({ status: RequestOriginsStatus.Failed; retry: () => void } & HeldOriginSelection)
	| { status: RequestOriginsStatus.Ready; control: OriginFilterControl };

/** The groups chosen, shown or not: a filter that is loading or failed still holds them. */
export const originSelectionOf = (state: OriginFilterState): readonly RequestOriginGroup[] => {
	switch (state.status) {
		case RequestOriginsStatus.Ready:
			return state.control.selected;
		case RequestOriginsStatus.Loading:
		case RequestOriginsStatus.Failed:
			return state.selected;
		case RequestOriginsStatus.Unavailable:
			// The department gets no origins, so there is nothing to choose.
			return [];
		default: {
			const unhandled: never = state;
			throw new Error(`Unhandled origin filter state ${JSON.stringify(unhandled)}`);
		}
	}
};

/** Clears the chosen groups in every state, so a "clear filters" never leaves a hidden choice to reapply later. */
export const clearOriginSelection = (state: OriginFilterState): void => {
	switch (state.status) {
		case RequestOriginsStatus.Ready:
			state.control.onChange([]);
			return;
		case RequestOriginsStatus.Loading:
		case RequestOriginsStatus.Failed:
			state.clear();
			return;
		case RequestOriginsStatus.Unavailable:
			return;
		default: {
			const unhandled: never = state;
			throw new Error(`Unhandled origin filter state ${JSON.stringify(unhandled)}`);
		}
	}
};

/** Each group's glyph in the Origin filter: the kind most of it is. */
export const ORIGIN_GROUP_ICON: Readonly<Record<RequestOriginGroup, SvgIconComponent>> = {
	[RequestOriginGroup.Automation]: BoltOutlinedIcon,
	[RequestOriginGroup.People]: PersonOutlineOutlinedIcon,
	[RequestOriginGroup.External]: HubOutlinedIcon,
};

/** Which Requests layout a filter change came from, for `request_origin_filter_changed`. */
export enum RequestOriginFilterLayout {
	Classic = "classic",
	Gaps = "gaps",
}

/** The +N an opening adds reads in the brand tint; a −N removal stays neutral, so the list's eye goes to causes. */
export const DELTA_CHIP_TONE = { OPENED: "primary", REMOVED: "neutral" } as const;
